import { useEffect, useMemo, useState } from 'react';
import { SimpleErrorResponse } from '@/models/Error';
import useMonolithApiQuery from '@/hooks/api/useMonolithApiQuery';
import Pipelinables from '@/models/Pipelineables';
import { StackAndMachineConfiguration } from '@/pages/App/components/StartBuildDialog/types';
import transformStacksAndMachineTypes from '@/pages/App/hooks/transformStacksAndMachineTypes';
import { getStartBuildDialogDataPath } from '@/utils/monolithApiRouteService';

type StartBuildDialogData = {
  hasTriggerMap: false;
  workflows: Pipelinables[];
  pipelines: Pipelinables[];
  stackAndMachineConfigurationJson: string;
};

const useStartBuildDialogData = ({ appSlug, params }: { appSlug: string; params?: Record<string, string> }) => {
  const [isEnabled, setEnabled] = useState(true);
  const { data: startBuildDialogData, isLoading } = useMonolithApiQuery<StartBuildDialogData, SimpleErrorResponse>(
    ['app', appSlug, params, 'start-build-dialog-data'],
    getStartBuildDialogDataPath(appSlug, {
      params,
    }),
    {
      enabled: isEnabled,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    },
  );

  let stackAndMachineConfiguration: StackAndMachineConfiguration = {
    availableStacks: {},
    availableMachineTypeConfigs: {},
  };

  try {
    if (startBuildDialogData?.stackAndMachineConfigurationJson) {
      const parsed = JSON.parse(startBuildDialogData?.stackAndMachineConfigurationJson);
      stackAndMachineConfiguration = transformStacksAndMachineTypes(parsed);
    }
  } catch (e) {
    console.error('Error parsing stack and machine configuration', e);
  }

  const hasTriggerMap = useMemo(() => {
    return Boolean(startBuildDialogData?.hasTriggerMap);
  }, [startBuildDialogData?.hasTriggerMap]);

  const pipelines = useMemo(() => {
    return startBuildDialogData?.pipelines?.map((p) => p.id) ?? [];
  }, [startBuildDialogData?.pipelines]);

  const workflows = useMemo(() => {
    return startBuildDialogData?.workflows?.map((w) => w.id) ?? [];
  }, [startBuildDialogData?.workflows]);

  useEffect(() => {
    if (!params) {
      setEnabled(false);
    }
  }, [params]);

  return {
    hasTriggerMap,
    isLoading,
    pipelines,
    workflows,
    stackAndMachineConfiguration,
  };
};

export default useStartBuildDialogData;
